import { LucideMail, LucidePhone, LucideSmartphone } from 'lucide-react'
import { fmtPhoneNumber } from '~/lib'
import {
  ActivityRelatedType,
  ActivityType,
  type Client,
  type Listing,
} from '~/models'
import { createActivity } from '~/query'

type ListingBrokersProps = {
  className?: string
  client: Client
  listing: Listing
  headingLevel?: 2 | 3 | 4 | 5 | 6
}

export default function ListingBrokers(props: ListingBrokersProps) {
  const { className, client, listing, headingLevel = 4 } = props
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements

  return (
    <div className={className}>
      {client.contact && (
        <div className="mb-5 border-b border-b-gray-200 pb-5">
          <HeadingTag className="mb-2 text-xl font-medium">{`Economic Development Contact`}</HeadingTag>
          <p className="font-semibold">{client.contact.name}</p>
          {client.contact.title && <p>{client.contact.title}</p>}
          {client.contact.email && (
            <div className="mt-2 flex items-center">
              <LucideMail className="mr-3 h-5 w-5 text-gray-400" />
              <a
                href={`mailto:${client.contact.email}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:text-black"
                onClick={() => {
                  createActivity({
                    type: ActivityType.contact,
                    relatedType: ActivityRelatedType.listing,
                    relatedId: listing.id,
                    meta: {
                      type: 'ecDevContact',
                      method: 'email',
                      ...client.contact!,
                    },
                  })
                }}>
                {client.contact.email}
              </a>
            </div>
          )}
          {client.contact.phone && (
            <div className="mt-2 flex items-center">
              <LucidePhone className="mr-3 h-5 w-5 text-gray-400" />
              <a
                href={`tel:${client.contact.phone}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:text-black"
                onClick={() => {
                  createActivity({
                    type: ActivityType.contact,
                    relatedType: ActivityRelatedType.listing,
                    relatedId: listing.id,
                    meta: {
                      type: 'ecDevContact',
                      method: 'phone',
                      ...client.contact!,
                    },
                  })
                }}>
                {fmtPhoneNumber(client.contact.phone)}
              </a>
            </div>
          )}
        </div>
      )}
      <HeadingTag className="mb-2 text-xl font-medium">
        {listing.organization.name}
      </HeadingTag>
      {listing.profiles.map((broker, index) => {
        return (
          <div key={index} className="mb-4 last:mb-0">
            <p className="mb-1 font-semibold">
              {[broker.first_name, broker.last_name].filter((v) => v).join(' ')}
            </p>
            {broker.email && (
              <div className="mb-2 flex items-center">
                <LucideMail className="mr-3 h-5 w-5 text-gray-400" />
                <a
                  href={`mailto:${broker.email}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:text-black"
                  onClick={() => {
                    createActivity({
                      type: ActivityType.contact,
                      relatedType: ActivityRelatedType.listing,
                      relatedId: listing.id,
                      meta: { type: 'broker', method: 'email', ...broker },
                    })
                  }}>
                  {broker.email}
                </a>
              </div>
            )}
            {broker.office && (
              <div className="mb-2 flex items-center">
                <LucidePhone className="mr-3 h-5 w-5 text-gray-400" />
                <a
                  href={`tel:${broker.office}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:text-black"
                  onClick={() => {
                    createActivity({
                      type: ActivityType.contact,
                      relatedType: ActivityRelatedType.listing,
                      relatedId: listing.id,
                      meta: { type: 'broker', method: 'office', ...broker },
                    })
                  }}>
                  {fmtPhoneNumber(broker.office)}
                </a>
              </div>
            )}
            {broker.mobile && (
              <div className="mb-2 flex items-center">
                <LucideSmartphone className="mr-3 h-5 w-5 text-gray-400" />
                <a
                  href={`tel:${broker.mobile}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:text-black"
                  onClick={() => {
                    createActivity({
                      type: ActivityType.contact,
                      relatedType: ActivityRelatedType.listing,
                      relatedId: listing.id,
                      meta: { type: 'broker', method: 'mobile', ...broker },
                    })
                  }}>
                  {fmtPhoneNumber(broker.mobile)}
                </a>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
